import React from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Drawer, Input, Select, Slider, Space, Tooltip } from "antd";
import BufferedNumberInput from "./BufferedNumberInput";
import {
  DEFAULT_BACKGROUND_OPACITY,
  DEFAULT_BANK_DRAWER_WIDTH,
  DEFAULT_BANK_LIFT_VISUAL,
  DEFAULT_ROTATION,
  DEFAULT_SYNC_ROTATIONS,
  DEFAULT_WAYPOINT_BACKGROUND_SIZE,
  DEFAULT_FONT_SIZE,
  getDefaultBankWidthForLiftCount,
  LINKED_LIFTS_TOOLTIP,
  MIN_SHAPE_SIZE,
} from "./constants";
import type { BankEditorDrawerProps, Waypoint } from "./types";

function createBankLift(
  name: string,
  index: number,
  linkedLiftId: string,
  linkedBankName: string,
): Waypoint {
  return {
    id: `bank-lift-${Date.now()}-${index}`,
    nickname: name,
    x: null,
    y: null,
    ...DEFAULT_BANK_LIFT_VISUAL,
    linkedLift: linkedLiftId,
    linkedBank: linkedBankName,
    displayName: true,
  };
}

function BankEditorDrawer({
  open,
  onClose,
  onSave,
  onDelete,
  loading = false,
  mode,
  isPinMode,
  bankWaypoint,
  banks,
  onBankWaypointChange,
  onActiveLiftSelect,
  onTogglePinMode,
}: BankEditorDrawerProps) {
  const selectedBank = banks.find((bank) => bank.name === bankWaypoint.bankName);
  const hasBankSelected = Boolean(selectedBank);
  const isPinned = bankWaypoint.x !== null && bankWaypoint.y !== null;
  const bankOptions = banks.map((bank) => ({ value: bank.name, label: bank.name }));
  const bankSyncRotations = bankWaypoint.syncRotations ?? DEFAULT_SYNC_ROTATIONS;
  const lockAspectRatio = Boolean(bankWaypoint.linkLifts);

  const getLockedSizeFromWidth = (
    currentWidth: number,
    currentHeight: number,
    nextWidth: number,
  ) => {
    const safeWidth = Math.max(currentWidth, MIN_SHAPE_SIZE);
    const safeHeight = Math.max(currentHeight, MIN_SHAPE_SIZE);
    const ratio = safeHeight / safeWidth;

    return {
      backgroundWidth: nextWidth,
      backgroundHeight: Math.max(MIN_SHAPE_SIZE, Math.round(nextWidth * ratio)),
    };
  };

  const getLockedSizeFromHeight = (
    currentWidth: number,
    currentHeight: number,
    nextHeight: number,
  ) => {
    const safeWidth = Math.max(currentWidth, MIN_SHAPE_SIZE);
    const safeHeight = Math.max(currentHeight, MIN_SHAPE_SIZE);
    const ratio = safeWidth / safeHeight;

    return {
      backgroundWidth: Math.max(MIN_SHAPE_SIZE, Math.round(nextHeight * ratio)),
      backgroundHeight: nextHeight,
    };
  };

  const updateLift = (liftIndex: number, nextLift: Waypoint) => {
    onBankWaypointChange({
      ...bankWaypoint,
      lifts: bankWaypoint.lifts.map((lift, index) => (index === liftIndex ? nextLift : lift)),
    });
  };

  return (
    <Drawer
      title={mode === "edit" ? "Edit Bank" : "Add Bank"}
      placement="right"
      onClose={onClose}
      open={open}
      mask={false}
      width={DEFAULT_BANK_DRAWER_WIDTH}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {mode === "edit" && onDelete && (
            <Button danger onClick={onDelete} disabled={loading}>
              Delete
            </Button>
          )}
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onSave}
            disabled={!hasBankSelected || !isPinned || loading}
            loading={loading}
          >
            Save
          </Button>
        </div>
      }
    >
      <div style={{ pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.7 : 1 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 8 }}>
              Select Bank <span style={{ color: "#ff4d4f" }}>*</span>
            </div>
            <Select
              placeholder="Select bank"
              style={{ width: "100%" }}
              options={bankOptions}
              value={bankWaypoint.bankName}
              onChange={(value) => {
                const bank = banks.find((item) => item.name === value);
                if (!bank) {
                  return;
                }

                onBankWaypointChange({
                  ...bankWaypoint,
                  bankName: bank.name,
                  nickname: bank.name,
                  backgroundWidth: getDefaultBankWidthForLiftCount(bank.lifts.length),
                  lifts: bank.lifts.map((liftName, index) =>
                    createBankLift(liftName, index, bank.liftIds?.[index] || liftName, bank.name),
                  ),
                });
              }}
            />
          </div>

          {hasBankSelected && !isPinned && (
            <Button type={isPinMode ? "primary" : "default"} onClick={onTogglePinMode}>
              Click to Pin
            </Button>
          )}

          {hasBankSelected && isPinned && (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Bank Anchor</div>
                <Space size="small" style={{ width: "100%", alignItems: "flex-end" }}>
                  <div>
                    <div style={{ marginBottom: 6 }}>x</div>
                    <BufferedNumberInput
                      style={{ width: 92 }}
                      value={bankWaypoint.x}
                      fallback={0}
                      onCommit={(value) => {
                        onBankWaypointChange({ ...bankWaypoint, x: value });
                      }}
                      placeholder="x"
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>y</div>
                    <BufferedNumberInput
                      style={{ width: 92 }}
                      value={bankWaypoint.y}
                      fallback={0}
                      onCommit={(value) => {
                        onBankWaypointChange({ ...bankWaypoint, y: value });
                      }}
                      placeholder="y"
                    />
                  </div>
                  <Button type={isPinMode ? "primary" : "default"} onClick={onTogglePinMode}>
                    Click to Pin
                  </Button>
                </Space>
              </div>

              <div
                style={{
                  padding: 12,
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  backgroundColor: "#fafafa",
                }}
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Space size="small" style={{ width: "100%" }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>Width (px)</div>
                      <BufferedNumberInput
                        min={MIN_SHAPE_SIZE}
                        value={bankWaypoint.backgroundWidth}
                        fallback={220}
                        onCommit={(value) => {
                          if (lockAspectRatio) {
                            onBankWaypointChange({
                              ...bankWaypoint,
                              ...getLockedSizeFromWidth(
                                bankWaypoint.backgroundWidth,
                                bankWaypoint.backgroundHeight,
                                value,
                              ),
                            });
                            return;
                          }

                          onBankWaypointChange({ ...bankWaypoint, backgroundWidth: value });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6 }}>Height (px)</div>
                      <BufferedNumberInput
                        min={MIN_SHAPE_SIZE}
                        value={bankWaypoint.backgroundHeight}
                        fallback={120}
                        onCommit={(value) => {
                          if (lockAspectRatio) {
                            onBankWaypointChange({
                              ...bankWaypoint,
                              ...getLockedSizeFromHeight(
                                bankWaypoint.backgroundWidth,
                                bankWaypoint.backgroundHeight,
                                value,
                              ),
                            });
                            return;
                          }

                          onBankWaypointChange({ ...bankWaypoint, backgroundHeight: value });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                  </Space>

                  <div>
                    <div style={{ marginBottom: 6 }}>Background Color</div>
                    <input
                      type="color"
                      value={bankWaypoint.backgroundColorHex}
                      onChange={(event) => {
                        onBankWaypointChange({
                          ...bankWaypoint,
                          backgroundColorHex: event.target.value,
                        });
                      }}
                      style={{
                        width: 52,
                        height: 32,
                        border: "none",
                        padding: 0,
                        background: "none",
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ marginBottom: 6 }}>Transparency (%)</div>
                    <Slider
                      min={0}
                      max={100}
                      value={bankWaypoint.backgroundOpacity}
                      onChange={(value) => {
                        onBankWaypointChange({
                          ...bankWaypoint,
                          backgroundOpacity:
                            typeof value === "number" ? value : DEFAULT_BACKGROUND_OPACITY,
                        });
                      }}
                      tooltip={{ formatter: (value) => `${value ?? 0}%` }}
                    />
                  </div>

                  <div>
                    <Checkbox
                      checked={bankWaypoint.linkLifts}
                      onChange={(event) => {
                        onBankWaypointChange({
                          ...bankWaypoint,
                          linkLifts: event.target.checked,
                        });
                      }}
                    >
                      Link Lifts
                    </Checkbox>
                    <Tooltip title={LINKED_LIFTS_TOOLTIP}>
                      <InfoCircleOutlined style={{ marginLeft: 8, color: "#1677ff" }} />
                    </Tooltip>
                  </div>

                  <div>
                    <Checkbox
                      checked={bankSyncRotations}
                      onChange={(event) => {
                        onBankWaypointChange({
                          ...bankWaypoint,
                          syncRotations: event.target.checked,
                        });
                      }}
                    >
                      Keep All Rotations Synced
                    </Checkbox>
                  </div>

                  <Space size="small" style={{ width: "100%" }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>Background (deg)</div>
                      <BufferedNumberInput
                        min={-360}
                        max={360}
                        value={bankWaypoint.backgroundRotationDeg ?? 0}
                        fallback={0}
                        onCommit={(value) => {
                          if (bankSyncRotations) {
                            onBankWaypointChange({
                              ...bankWaypoint,
                              backgroundRotationDeg: value,
                              nameRotationDeg: value,
                            });
                            return;
                          }

                          onBankWaypointChange({
                            ...bankWaypoint,
                            backgroundRotationDeg: value,
                          });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6 }}>Name (deg)</div>
                      <BufferedNumberInput
                        min={-360}
                        max={360}
                        value={bankWaypoint.nameRotationDeg ?? 0}
                        fallback={0}
                        onCommit={(value) => {
                          if (bankSyncRotations) {
                            onBankWaypointChange({
                              ...bankWaypoint,
                              backgroundRotationDeg: value,
                              nameRotationDeg: value,
                            });
                            return;
                          }

                          onBankWaypointChange({
                            ...bankWaypoint,
                            nameRotationDeg: value,
                          });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                  </Space>

                  <div>
                    <Checkbox
                      checked={bankWaypoint.displayName}
                      onChange={(event) => {
                        onBankWaypointChange({
                          ...bankWaypoint,
                          displayName: event.target.checked,
                        });
                      }}
                    >
                      Display Name
                    </Checkbox>
                  </div>

                  {bankWaypoint.displayName && (
                    <Space size="small" style={{ width: "100%", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ marginBottom: 6 }}>Font Size (px)</div>
                        <BufferedNumberInput
                          min={8}
                          max={48}
                          value={bankWaypoint.fontSize}
                          fallback={DEFAULT_FONT_SIZE}
                          onCommit={(value) => {
                            onBankWaypointChange({
                              ...bankWaypoint,
                              fontSize: value,
                            });
                          }}
                          style={{ width: 110 }}
                        />
                      </div>
                      <div>
                        <div style={{ marginBottom: 6 }}>Font Color</div>
                        <input
                          type="color"
                          value={bankWaypoint.fontColorHex}
                          onChange={(event) => {
                            onBankWaypointChange({
                              ...bankWaypoint,
                              fontColorHex: event.target.value,
                            });
                          }}
                          style={{
                            width: 52,
                            height: 24,
                            border: "none",
                            padding: 0,
                            background: "none",
                            display: "block",
                          }}
                        />
                      </div>
                    </Space>
                  )}
                </Space>
              </div>

              {bankWaypoint.lifts.map((lift, index) => {
                const syncRotations = lift.syncRotations ?? DEFAULT_SYNC_ROTATIONS;
                const selectedBankLiftOptions = (selectedBank?.lifts || []).map(
                  (liftName, liftOptionIndex) => ({
                    value: selectedBank?.liftIds?.[liftOptionIndex] || liftName,
                    label: liftName,
                  }),
                );
                const selectedLinkedLiftValue =
                  lift.linkedLift ||
                  selectedBankLiftOptions[index]?.value ||
                  selectedBank?.lifts[index] ||
                  undefined;

                return (
                  <div
                    key={lift.id}
                    style={{
                      padding: 12,
                      border: "1px solid #d9d9d9",
                      borderRadius: 8,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 10 }}>
                      Lift: {selectedBank?.lifts[index] || lift.nickname}
                    </div>
                    <Button
                      size="small"
                      onClick={() => {
                        onActiveLiftSelect(lift.id);
                      }}
                      style={{ marginBottom: 10 }}
                    >
                      Focus In Canvas
                    </Button>

                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                      <div>
                        <div style={{ marginBottom: 6 }}>Nickname</div>
                        <Input
                          value={lift.nickname}
                          onChange={(event) => {
                            updateLift(index, {
                              ...lift,
                              nickname: event.target.value || undefined,
                            });
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ marginBottom: 6 }}>Linked Lift</div>
                        <Select
                          style={{ width: "100%" }}
                          options={selectedBankLiftOptions}
                          value={selectedLinkedLiftValue}
                          disabled
                        />
                      </div>

                      <Space size="small" style={{ width: "100%" }}>
                        <div>
                          <div style={{ marginBottom: 6 }}>x</div>
                          <BufferedNumberInput
                            style={{ width: 92 }}
                            value={lift.x}
                            fallback={0}
                            onCommit={(value) => {
                              updateLift(index, { ...lift, x: value });
                            }}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 6 }}>y</div>
                          <BufferedNumberInput
                            style={{ width: 92 }}
                            value={lift.y}
                            fallback={0}
                            onCommit={(value) => {
                              updateLift(index, { ...lift, y: value });
                            }}
                          />
                        </div>
                      </Space>

                      <Space size="small" style={{ width: "100%" }}>
                        <div>
                          <div style={{ marginBottom: 6 }}>Width (px)</div>
                          <BufferedNumberInput
                            min={MIN_SHAPE_SIZE}
                            value={lift.backgroundWidth}
                            fallback={DEFAULT_WAYPOINT_BACKGROUND_SIZE}
                            onCommit={(value) => {
                              if (lockAspectRatio) {
                                updateLift(index, {
                                  ...lift,
                                  ...getLockedSizeFromWidth(
                                    lift.backgroundWidth,
                                    lift.backgroundHeight,
                                    value,
                                  ),
                                });
                                return;
                              }

                              updateLift(index, { ...lift, backgroundWidth: value });
                            }}
                            style={{ width: 110 }}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 6 }}>Height (px)</div>
                          <BufferedNumberInput
                            min={MIN_SHAPE_SIZE}
                            value={lift.backgroundHeight}
                            fallback={DEFAULT_WAYPOINT_BACKGROUND_SIZE}
                            onCommit={(value) => {
                              if (lockAspectRatio) {
                                updateLift(index, {
                                  ...lift,
                                  ...getLockedSizeFromHeight(
                                    lift.backgroundWidth,
                                    lift.backgroundHeight,
                                    value,
                                  ),
                                });
                                return;
                              }

                              updateLift(index, { ...lift, backgroundHeight: value });
                            }}
                            style={{ width: 110 }}
                          />
                        </div>
                      </Space>

                      <div>
                        <div style={{ marginBottom: 6 }}>Background Color</div>
                        <input
                          type="color"
                          value={lift.backgroundColorHex}
                          onChange={(event) => {
                            updateLift(index, { ...lift, backgroundColorHex: event.target.value });
                          }}
                          style={{
                            width: 52,
                            height: 24,
                            border: "none",
                            padding: 0,
                            background: "none",
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ marginBottom: 6 }}>Transparency (%)</div>
                        <Slider
                          min={0}
                          max={100}
                          value={lift.backgroundOpacity}
                          onChange={(value) => {
                            updateLift(index, {
                              ...lift,
                              backgroundOpacity:
                                typeof value === "number" ? value : DEFAULT_BACKGROUND_OPACITY,
                            });
                          }}
                          tooltip={{ formatter: (value) => `${value ?? 0}%` }}
                        />
                      </div>

                      <div>
                        <Checkbox
                          checked={lift.displayName}
                          onChange={(event) => {
                            updateLift(index, { ...lift, displayName: event.target.checked });
                          }}
                        >
                          Display Name
                        </Checkbox>
                      </div>

                      {lift.displayName && (
                        <Space size="small" style={{ width: "100%" }}>
                          <div>
                            <div style={{ marginBottom: 6 }}>Font Size (px)</div>
                            <BufferedNumberInput
                              min={8}
                              max={48}
                              value={lift.fontSize}
                              fallback={DEFAULT_FONT_SIZE}
                              onCommit={(value) => {
                                updateLift(index, { ...lift, fontSize: value });
                              }}
                              style={{ width: 110 }}
                            />
                          </div>
                          <div>
                            <div style={{ marginBottom: 6 }}>Font Color</div>
                            <input
                              type="color"
                              value={lift.fontColorHex}
                              onChange={(event) => {
                                updateLift(index, { ...lift, fontColorHex: event.target.value });
                              }}
                              style={{
                                width: 52,
                                height: 24,
                                border: "none",
                                padding: 0,
                                background: "none",
                              }}
                            />
                          </div>
                        </Space>
                      )}

                      <div>
                        <Checkbox
                          checked={syncRotations}
                          onChange={(event) => {
                            updateLift(index, { ...lift, syncRotations: event.target.checked });
                          }}
                        >
                          Keep All Rotations Synced
                        </Checkbox>
                      </div>

                      <Space size="small" style={{ width: "100%" }}>
                        <div>
                          <div style={{ marginBottom: 6 }}>Background (deg)</div>
                          <BufferedNumberInput
                            min={-360}
                            max={360}
                            value={lift.backgroundRotationDeg ?? DEFAULT_ROTATION}
                            fallback={DEFAULT_ROTATION}
                            onCommit={(nextRotation) => {
                              if (syncRotations) {
                                updateLift(index, {
                                  ...lift,
                                  backgroundRotationDeg: nextRotation,
                                  iconRotationDeg: nextRotation,
                                  nameRotationDeg: nextRotation,
                                });
                                return;
                              }

                              updateLift(index, { ...lift, backgroundRotationDeg: nextRotation });
                            }}
                            style={{ width: 110 }}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 6 }}>Icon (deg)</div>
                          <BufferedNumberInput
                            min={-360}
                            max={360}
                            value={lift.iconRotationDeg ?? DEFAULT_ROTATION}
                            fallback={DEFAULT_ROTATION}
                            onCommit={(nextRotation) => {
                              if (syncRotations) {
                                updateLift(index, {
                                  ...lift,
                                  backgroundRotationDeg: nextRotation,
                                  iconRotationDeg: nextRotation,
                                  nameRotationDeg: nextRotation,
                                });
                                return;
                              }

                              updateLift(index, { ...lift, iconRotationDeg: nextRotation });
                            }}
                            style={{ width: 110 }}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 6 }}>Name (deg)</div>
                          <BufferedNumberInput
                            min={-360}
                            max={360}
                            value={lift.nameRotationDeg ?? DEFAULT_ROTATION}
                            fallback={DEFAULT_ROTATION}
                            onCommit={(nextRotation) => {
                              if (syncRotations) {
                                updateLift(index, {
                                  ...lift,
                                  backgroundRotationDeg: nextRotation,
                                  iconRotationDeg: nextRotation,
                                  nameRotationDeg: nextRotation,
                                });
                                return;
                              }

                              updateLift(index, { ...lift, nameRotationDeg: nextRotation });
                            }}
                            style={{ width: 110 }}
                          />
                        </div>
                      </Space>
                    </Space>
                  </div>
                );
              })}
            </Space>
          )}
        </Space>
      </div>
    </Drawer>
  );
}

export default BankEditorDrawer;
